import i18n from '@/i18n';
import store from '@/store';
import { getProductSettingsByServerIds } from '@/services/product/settings/productSettings';
import httpClient from '@/api/httpClient';
import * as regionApi from '@/api/regionApi';
import { isCustomRegion } from '@/utils/regionUtils';
import mapService from '@/services/mapService';

const getRegionProducts = async (regionCode) => {
  const productKeys = await httpClient
    .get(`/api/products/?filter[region]=${regionCode}&filter[lang]=${i18n.locale}`)
    .then((resp) => resp.data.data);
  return getProductSettingsByServerIds({ serverIds: productKeys, i18n });
};

const admRegionService = {
  getRegionData: async (regionCode, options) => {
    const regionData = await regionApi.getRegionData({ regionCode, lang: i18n.locale, options });
    return {
      ...regionData,
      code: regionCode,
    };
  },
  loadRegionDataToStore: async (regionCode, options) => {
    const regionData = await admRegionService.getRegionData(regionCode, options);
    if (regionData) {
      store.commit('regions/SET_CURRENT_REGION', regionData);
      store.commit('regions/SET_REGION_SOURCE', 'catalog');
      store.commit('regions/SET_REGION_SOURCE_OPTIONS', {
        ...regionData,
        // code: regionCode,
      });
    }
  },
};

const customRegionService = {
  getRegionData: () => ({
    code: 'custom',
    name: i18n.t('catalog.customArea'),
  }),
  loadRegionDataToStore: async (regionCode, options) => {
    const regionData = customRegionService.getRegionData(regionCode, options);
    if (regionData) {
      store.commit('regions/SET_CURRENT_REGION', regionData);
    }
  },
};

const regionServiceAdapter = (regionCode) => (isCustomRegion(regionCode) ? customRegionService : admRegionService);

const getRegionData = (regionCode, options) => regionServiceAdapter(regionCode).getRegionData(regionCode, options);

const getRegionGeometry = async (regionCode, options) => {
  const regionGeometry = await regionApi.getRegionGeometry({ regionCode, options });
  // TODO: handle several features
  return (
    regionGeometry &&
    regionGeometry.geometry.filter((x) => x.geometry && ['MultiPolygon', 'Polygon'].indexOf(x.geometry.type) !== -1)[0]
  );
};

const loadRegionGeometryToStore = async (regionCode, options) => {
  const regionGeometry = await getRegionGeometry(regionCode, options);
  if (regionGeometry)
    store.commit('regions/SET_CURRENT_REGION_GEOMETRY', {
      geojson: regionGeometry,
      wkt: undefined,
      customRegionMeta: undefined,
    });
};

const updateRegionGeometry = (geometryObject) => {
  const wkt = geometryObject && geometryObject.geometry ? mapService.drawnGeojsonToWkt(geometryObject.geometry) : '';
  const customRegionMeta = geometryObject?.meta;

  store.commit('regions/SET_CURRENT_REGION_GEOMETRY', {
    wkt,
    geojson: geometryObject && geometryObject.geometry,
    customRegionMeta,
  });

  if (geometryObject?.meta) {
    const { source, regionData } = geometryObject.meta;
    store.commit('regions/SET_REGION_SOURCE', source);
    store.commit('regions/SET_REGION_SOURCE_OPTIONS', regionData);
  } else {
    store.commit('regions/SET_REGION_SOURCE', undefined);
    store.commit('regions/SET_REGION_SOURCE_OPTIONS', undefined);
  }
};

const loadRegionDataToStore = (regionCode, options) =>
  regionServiceAdapter(regionCode).loadRegionDataToStore(regionCode, options);

const resetRegionDataAndGeometry = () => {
  store.commit('regions/SET_CURRENT_REGION_CODE', undefined);
  store.commit('regions/SET_CURRENT_REGION', undefined);
  store.commit('regions/SET_CURRENT_REGION_GEOMETRY', undefined);
  store.commit('map/SET_BOUNDARY_FILE_VALUE', null);
  store.commit('map/SET_GEOCODER_VALUE', null);
  store.commit('regions/SET_REGION_SOURCE', undefined);
  store.commit('regions/SET_REGION_SOURCE_OPTIONS', undefined);
};

export default {
  getRegionData,
  loadRegionDataToStore,
  getRegionProducts,
  loadRegionGeometryToStore,
  getRegionGeometry,
  resetRegionDataAndGeometry,
  updateRegionGeometry,
};
